import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ShoppingCart,
  Euro,
  Users,
  Package,
  Star,
  ChevronRight,
  Calendar,
  TrendingUp,
  Search,
  Filter,
  Download,
  BarChart3,
} from 'lucide-react';
import apiService from '../services/api';
import {
  PerformanceScopeBanner,
  OpsRegistreBanner,
  PerformanceOpsGapBanner,
  PerformanceOfficialKpis,
} from './AnalyticsRegistreBanner';
import {
  buildCaInsights,
  buildCommandesInsights,
  filterCatalogItems,
  filterCommandes,
  formatEuro,
  computeCommandeTotal,
  isCommandeAnnulee,
} from '../utils/overviewStats';

const METRIC_CONFIG = {
  commandes: {
    title: 'Commandes',
    subtitle: 'Performance officielle et registre opérationnel',
    icon: ShoppingCart,
    gradient: 'from-blue-500 to-indigo-600',
    tab: 'commandes',
    tabLabel: 'Gestion des commandes',
  },
  ca: {
    title: "Chiffre d'affaires",
    subtitle: 'Performance officielle et analyse registre ops',
    icon: Euro,
    gradient: 'from-emerald-500 to-teal-600',
    tab: 'analytics',
    tabLabel: 'Analytics complets',
  },
  prestations: {
    title: 'Prestations',
    subtitle: 'Catalogue et répartition par type',
    icon: Users,
    gradient: 'from-violet-500 to-purple-600',
    tab: 'prestations',
    tabLabel: 'Gérer les prestations',
  },
  produits: {
    title: 'Produits',
    subtitle: 'Catalogue et TVA appliquée',
    icon: Package,
    gradient: 'from-fuchsia-500 to-pink-600',
    tab: 'produits',
    tabLabel: 'Catalogue produits',
  },
  note: {
    title: 'Avis & note',
    subtitle: 'Réputation et retours clients',
    icon: Star,
    gradient: 'from-amber-400 to-orange-500',
    tab: 'avis',
    tabLabel: 'Gestion des avis',
  },
};

const PERIODE_OPTIONS = [
  { value: 'all', label: 'Toute la période' },
  { value: '7j', label: '7 derniers jours' },
  { value: '30j', label: '30 derniers jours' },
  { value: '90j', label: '90 derniers jours' },
];

const KpiCard = ({ label, value, sub, accent = 'blue' }) => {
  const accents = {
    blue: 'border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-900',
    green: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-900',
    purple: 'border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 text-violet-900',
    amber: 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-900',
    gray: 'border-gray-200 bg-gradient-to-br from-gray-50 to-slate-50 text-gray-900',
  };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${accents[accent] || accents.gray}`}>
      <p className="text-xs font-medium opacity-80 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
};

const DistributionBar = ({ label, count, total, color = 'bg-blue-500' }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className="text-gray-500">
          {count} ({pct}%)
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const FilterToolbar = ({ search, onSearch, children, onReset, resultCount }) => (
  <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50 p-4 space-y-3">
    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Rechercher…"
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
        >
          Réinitialiser
        </button>
      )}
    </div>
    {resultCount != null && (
      <p className="text-xs text-gray-500">
        <Filter className="inline h-3 w-3 mr-1" />
        {resultCount} résultat{resultCount !== 1 ? 's' : ''} affiché{resultCount !== 1 ? 's' : ''}
      </p>
    )}
  </div>
);

const OverviewStatDetail = ({
  metric,
  commandes = [],
  prestations = [],
  produits = [],
  analytics = {},
  performanceOverview = null,
  commercant,
  onBack,
  onOpenTab,
  onSelectCommande,
  onSelectProduit,
  onSelectPrestation,
}) => {
  const config = METRIC_CONFIG[metric];
  const [search, setSearch] = useState('');
  const [periode, setPeriode] = useState('all');
  const [statutFilter, setStatutFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [catalogTypeFilter, setCatalogTypeFilter] = useState('');
  const [avisStats, setAvisStats] = useState({ moyenne: 0, total: 0 });
  const [avisList, setAvisList] = useState([]);
  const [avisLoading, setAvisLoading] = useState(metric === 'note');

  const resetFilters = () => {
    setSearch('');
    setPeriode('all');
    setStatutFilter('');
    setTypeFilter('');
    setCatalogTypeFilter('');
  };

  useEffect(() => {
    if (metric !== 'note' || !commercant?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setAvisLoading(true);
        const response = await apiService.request(`/avis/commercant/${commercant.id}`);
        if (!cancelled) {
          setAvisStats(response?.stats || { moyenne: commercant?.note || 0, total: 0 });
          setAvisList(Array.isArray(response?.data) ? response.data : []);
        }
      } catch {
        if (!cancelled) {
          setAvisStats({ moyenne: commercant?.note || 0, total: 0 });
          setAvisList([]);
        }
      } finally {
        if (!cancelled) setAvisLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metric, commercant?.id, commercant?.note]);

  const filteredCommandes = useMemo(
    () => filterCommandes(commandes, { search, statut: statutFilter, type: typeFilter, periode }),
    [commandes, search, statutFilter, typeFilter, periode],
  );

  const commandesInsights = useMemo(
    () => buildCommandesInsights(filteredCommandes),
    [filteredCommandes],
  );

  const caInsights = useMemo(() => buildCaInsights(filteredCommandes), [filteredCommandes]);

  const filteredPrestations = useMemo(
    () => filterCatalogItems(prestations, { search, type: catalogTypeFilter }),
    [prestations, search, catalogTypeFilter],
  );

  const filteredProduits = useMemo(
    () => filterCatalogItems(produits, { search }),
    [produits, search],
  );

  const statutsDisponibles = useMemo(() => {
    const set = new Set(commandes.map((c) => (c.statut || '').toUpperCase()).filter(Boolean));
    return [...set].sort();
  }, [commandes]);

  const exportCommandesCsv = () => {
    const rows = [
      ['Numero', 'Type', 'Date', 'Montant TTC', 'Statut', 'Client'].join(';'),
      ...filteredCommandes.map((cmd) =>
        [
          cmd.numero || cmd.id,
          cmd.type === 'RENDEZ_VOUS' ? 'RDV' : 'Commande',
          new Date(cmd.dateCommande || cmd.createdAt).toLocaleDateString('fr-FR'),
          computeCommandeTotal(cmd).toFixed(2),
          cmd.statut,
          [cmd.user?.prenom, cmd.user?.nom].filter(Boolean).join(' '),
        ].join(';'),
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commandes-${commercant?.nom || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!config) return null;

  const Icon = config.icon;

  const renderCommandes = () => (
    <div className="space-y-5">
      {performanceOverview && (
        <>
          <PerformanceScopeBanner
            resolver={performanceOverview.resolver}
            periode={performanceOverview.periode}
          />
          <PerformanceOfficialKpis performanceOverview={performanceOverview} />
        </>
      )}
      <OpsRegistreBanner />
      <PerformanceOpsGapBanner
        performanceVolume={performanceOverview?.kpi?.volume?.value ?? 0}
        opsListCount={commandes.length}
      />

      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Registre opérationnel (liste filtrée)</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Lignes affichées"
          value={commandesInsights.totalToutes}
          sub="Tous statuts confondus"
          accent="blue"
        />
        <KpiCard
          label="Hors annulées"
          value={commandesInsights.total}
          sub="Registre ops, sans filtre paiement"
          accent="green"
        />
        <KpiCard label="Annulées" value={commandesInsights.annulees} accent="gray" />
        <KpiCard
          label="Panier moyen ops"
          value={formatEuro(commandesInsights.panierMoyen)}
          sub="Sur lignes hors annulées"
          accent="purple"
        />
      </div>

      <FilterToolbar
        search={search}
        onSearch={setSearch}
        onReset={resetFilters}
        resultCount={filteredCommandes.length}
      >
        <select
          value={periode}
          onChange={(e) => setPeriode(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        >
          {PERIODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={statutFilter}
          onChange={(e) => setStatutFilter(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        >
          <option value="">Tous statuts</option>
          {statutsDisponibles.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        >
          <option value="">Tous types</option>
          <option value="RDV">Rendez-vous</option>
          <option value="PRODUIT">Commandes produits</option>
        </select>
        <button
          type="button"
          onClick={exportCommandesCsv}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </FilterToolbar>

      {Object.keys(commandesInsights.parStatut).length > 0 && (
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            Répartition par statut
          </h4>
          <div className="space-y-3">
            {Object.entries(commandesInsights.parStatut)
              .sort((a, b) => b[1] - a[1])
              .map(([statut, count], i) => (
                <DistributionBar
                  key={statut}
                  label={statut.replace(/_/g, ' ')}
                  count={count}
                  total={commandesInsights.totalToutes}
                  color={['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500'][i % 5]}
                />
              ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Liste détaillée</h4>
          {onSelectCommande && (
            <span className="text-xs text-gray-500">Cliquez sur une ligne pour ouvrir le détail</span>
          )}
        </div>
        {filteredCommandes.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Aucune commande ne correspond aux filtres.</p>
        ) : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-4 py-2">Réf.</th>
                  <th className="px-4 py-2">Client</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">TTC</th>
                  <th className="px-4 py-2">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCommandes.map((cmd) => (
                  <tr
                    key={cmd.id}
                    className={`hover:bg-blue-50/70 transition-colors ${
                      onSelectCommande ? 'cursor-pointer' : ''
                    } ${isCommandeAnnulee(cmd) ? 'bg-red-50/30' : ''}`}
                    onClick={() => onSelectCommande?.(cmd)}
                    onKeyDown={(e) => {
                      if (onSelectCommande && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onSelectCommande(cmd);
                      }
                    }}
                    tabIndex={onSelectCommande ? 0 : undefined}
                    role={onSelectCommande ? 'button' : undefined}
                  >
                    <td className="px-4 py-2 font-medium">{cmd.numero || cmd.id?.slice(0, 8)}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {[cmd.user?.prenom, cmd.user?.nom].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          cmd.type === 'RENDEZ_VOUS'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {cmd.type === 'RENDEZ_VOUS' ? 'RDV' : 'Produit'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {new Date(cmd.dateCommande || cmd.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-2 font-semibold">{formatEuro(computeCommandeTotal(cmd))}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          isCommandeAnnulee(cmd)
                            ? 'bg-red-100 text-red-800'
                            : cmd.statut === 'LIVREE'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {cmd.statut}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderCa = () => (
    <div className="space-y-5">
      {performanceOverview && (
        <>
          <PerformanceScopeBanner
            resolver={performanceOverview.resolver}
            periode={performanceOverview.periode}
          />
          <PerformanceOfficialKpis performanceOverview={performanceOverview} />
        </>
      )}
      <OpsRegistreBanner />
      <PerformanceOpsGapBanner
        performanceVolume={performanceOverview?.kpi?.volume?.value ?? 0}
        opsListCount={commandes.length}
      />

      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Analyse registre opérationnel</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard label="CA ops TTC" value={formatEuro(caInsights.ca)} accent="green" />
        <KpiCard label="Panier moyen ops" value={formatEuro(caInsights.panierMoyen)} accent="blue" />
        <KpiCard label="Transactions ops" value={caInsights.total} accent="purple" />
      </div>

      <FilterToolbar search={search} onSearch={setSearch} onReset={resetFilters} resultCount={filteredCommandes.length}>
        <select
          value={periode}
          onChange={(e) => setPeriode(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        >
          {PERIODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </FilterToolbar>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-200 p-4 bg-gradient-to-br from-emerald-50 to-white">
          <h4 className="text-sm font-semibold text-emerald-900 mb-3">Répartition du CA</h4>
          <DistributionBar
            label="Rendez-vous / prestations"
            count={Math.round(caInsights.caRdv)}
            total={Math.round(caInsights.ca) || 1}
            color="bg-emerald-500"
          />
          <div className="mt-3" />
          <DistributionBar
            label="Commandes produits"
            count={Math.round(caInsights.caProduits)}
            total={Math.round(caInsights.ca) || 1}
            color="bg-teal-500"
          />
          <p className="text-xs text-emerald-800 mt-3">
            RDV : {formatEuro(caInsights.caRdv)} · Produits : {formatEuro(caInsights.caProduits)}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            CA par mois
          </h4>
          {caInsights.moisSorted.length === 0 ? (
            <p className="text-sm text-gray-500">Pas assez de données sur la période.</p>
          ) : (
            <div className="space-y-3">
              {caInsights.moisSorted.map((m, i) => (
                <DistributionBar
                  key={m.label}
                  label={`${m.label} (${m.count})`}
                  count={Math.round(m.ca)}
                  total={Math.round(caInsights.ca) || 1}
                  color={['bg-emerald-500', 'bg-green-500', 'bg-teal-500', 'bg-cyan-500'][i % 4]}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPrestations = () => {
    const tvaBreakdown = filteredPrestations.reduce((acc, p) => {
      const t = p.tauxTVA ?? 20;
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total" value={filteredPrestations.length} accent="purple" />
          {['RENDEZ_VOUS', 'CRENEAUX', 'RESERVATION'].map((type) => {
            const n = filteredPrestations.filter((p) => p.typePrestation === type).length;
            if (!n && catalogTypeFilter !== type) return null;
            return (
              <KpiCard
                key={type}
                label={type.replace('_', ' ')}
                value={n}
                accent="gray"
              />
            );
          })}
        </div>

        <FilterToolbar
          search={search}
          onSearch={setSearch}
          onReset={resetFilters}
          resultCount={filteredPrestations.length}
        >
          <select
            value={catalogTypeFilter}
            onChange={(e) => setCatalogTypeFilter(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Tous types</option>
            <option value="RENDEZ_VOUS">Rendez-vous</option>
            <option value="CRENEAUX">Créneaux</option>
            <option value="RESERVATION">Réservation</option>
          </select>
        </FilterToolbar>

        {Object.keys(tvaBreakdown).length > 1 && (
          <div className="rounded-xl border border-violet-200 p-4 bg-violet-50/50">
            <h4 className="text-sm font-semibold text-violet-900 mb-2">Répartition TVA (filtré)</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(tvaBreakdown).map(([taux, n]) => (
                <span
                  key={taux}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-white border border-violet-200 text-violet-800"
                >
                  {taux} % : {n} prestation{n > 1 ? 's' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
          {filteredPrestations.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">Aucune prestation trouvée.</p>
          ) : (
            <div className="overflow-x-auto">
              <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500">
                {onSelectPrestation && 'Cliquez sur une ligne pour modifier la prestation'}
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-violet-50 text-left text-xs uppercase text-violet-700">
                  <tr>
                    <th className="px-4 py-2">Nom</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Prix TTC</th>
                    <th className="px-4 py-2">TVA</th>
                    <th className="px-4 py-2">Durée</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPrestations.map((p) => (
                    <tr
                      key={p.id}
                      className={`hover:bg-violet-50/70 transition-colors ${onSelectPrestation ? 'cursor-pointer' : ''}`}
                      onClick={() => onSelectPrestation?.(p)}
                      onKeyDown={(e) => {
                        if (onSelectPrestation && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onSelectPrestation(p);
                        }
                      }}
                      tabIndex={onSelectPrestation ? 0 : undefined}
                      role={onSelectPrestation ? 'button' : undefined}
                    >
                      <td className="px-4 py-2 font-medium">{p.nom}</td>
                      <td className="px-4 py-2">{p.typePrestation || '—'}</td>
                      <td className="px-4 py-2">{formatEuro(p.prix)}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            p.tauxTVA === 5.5
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {p.tauxTVA ?? 20} %
                        </span>
                      </td>
                      <td className="px-4 py-2">{p.dureeMinutes ? `${p.dureeMinutes} min` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProduits = () => {
    const tvaBreakdown = filteredProduits.reduce((acc, p) => {
      const t = p.tauxTVA ?? 20;
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="space-y-5">
        <KpiCard label="Produits affichés" value={filteredProduits.length} accent="purple" />
        <FilterToolbar search={search} onSearch={setSearch} onReset={resetFilters} resultCount={filteredProduits.length} />
        {Object.keys(tvaBreakdown).length > 1 && (
          <div className="rounded-xl border border-fuchsia-200 p-4 bg-fuchsia-50/50">
            <h4 className="text-sm font-semibold text-fuchsia-900 mb-2">Répartition TVA (filtré)</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(tvaBreakdown).map(([taux, n]) => (
                <span
                  key={taux}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-white border border-fuchsia-200"
                >
                  {taux} % : {n} produit{n > 1 ? 's' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
          {filteredProduits.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">Aucun produit trouvé.</p>
          ) : (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500 sticky top-0 z-10">
                {onSelectProduit && 'Cliquez sur une ligne pour modifier le produit'}
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-fuchsia-50 text-left text-xs uppercase text-fuchsia-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-2">Nom</th>
                    <th className="px-4 py-2">Prix TTC</th>
                    <th className="px-4 py-2">TVA</th>
                    <th className="px-4 py-2">Dispo.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProduits.map((p) => (
                    <tr
                      key={p.id}
                      className={`hover:bg-fuchsia-50/70 transition-colors ${onSelectProduit ? 'cursor-pointer' : ''}`}
                      onClick={() => onSelectProduit?.(p)}
                      onKeyDown={(e) => {
                        if (onSelectProduit && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onSelectProduit(p);
                        }
                      }}
                      tabIndex={onSelectProduit ? 0 : undefined}
                      role={onSelectProduit ? 'button' : undefined}
                    >
                      <td className="px-4 py-2 font-medium">{p.nom}</td>
                      <td className="px-4 py-2">{formatEuro(p.prix)}</td>
                      <td className="px-4 py-2">{p.tauxTVA ?? 20} %</td>
                      <td className="px-4 py-2">{p.disponible !== false ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNote = () => {
    const filteredAvis = avisList.filter((a) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [a.commentaire, a.user?.nom, a.user?.prenom, String(a.note)].join(' ').toLowerCase().includes(q);
    });

    const parNote = [5, 4, 3, 2, 1].map((n) => ({
      note: n,
      count: filteredAvis.filter((a) => Math.round(a.note) === n).length,
    }));

    return (
      <div className="space-y-5">
        {avisLoading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard
                label="Note moyenne"
                value={`${(avisStats.moyenne ?? analytics.noteMoyenne ?? 0).toFixed(1)}/5`}
                accent="amber"
              />
              <KpiCard label="Nombre d'avis" value={avisStats.total ?? 0} accent="gray" />
              <KpiCard label="Note profil" value={`${(commercant?.note ?? 0).toFixed(1)}/5`} accent="blue" />
            </div>

            <FilterToolbar search={search} onSearch={setSearch} onReset={resetFilters} resultCount={filteredAvis.length} />

            <div className="rounded-xl border border-amber-200 p-4 bg-amber-50/30">
              <h4 className="text-sm font-semibold text-amber-900 mb-3">Distribution des notes</h4>
              <div className="space-y-2">
                {parNote.map(({ note, count }) => (
                  <DistributionBar
                    key={note}
                    label={`${note} étoile${note > 1 ? 's' : ''}`}
                    count={count}
                    total={filteredAvis.length || 1}
                    color="bg-amber-400"
                  />
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white divide-y max-h-[360px] overflow-y-auto">
              {filteredAvis.length === 0 ? (
                <p className="p-6 text-sm text-gray-500 text-center">Aucun avis.</p>
              ) : (
                filteredAvis.slice(0, 30).map((a) => (
                  <div key={a.id} className="p-4 hover:bg-amber-50/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-amber-700">{a.note}/5 ★</span>
                      <span className="text-xs text-gray-400">
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800">{a.commentaire || '—'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {[a.user?.prenom, a.user?.nom].filter(Boolean).join(' ')}
                    </p>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const contentMap = {
    commandes: renderCommandes,
    ca: renderCa,
    prestations: renderPrestations,
    produits: renderProduits,
    note: renderNote,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la vue d&apos;ensemble
        </button>
        <button
          type="button"
          onClick={() => onOpenTab(config.tab)}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm"
        >
          {config.tabLabel}
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>

      <div className={`rounded-2xl p-6 text-white bg-gradient-to-r ${config.gradient} shadow-lg`}>
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-white/20 backdrop-blur">
            <Icon className="h-10 w-10" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{config.title}</h2>
            <p className="text-sm text-white/90 flex items-center gap-1 mt-1">
              <TrendingUp className="h-3.5 w-3.5" />
              {config.subtitle} — {commercant?.nom}
            </p>
          </div>
        </div>
      </div>

      {contentMap[metric]?.()}
    </div>
  );
};

export default OverviewStatDetail;
export { METRIC_CONFIG };
